import { onBeforeUnmount, onMounted, watch } from 'vue';

import { markTicketRead } from '@/api/tickets';
import { isOwnMessage } from '@/tools/messages';
import type { WidgetMessage } from '@/api/types';
import type { Ref } from 'vue';

/**
 * How often an open thread says "still here".
 *
 * The API holds an agent's reply back from email only for a contact who had the
 * thread on screen within its read-grace window — minutes — so this has to be
 * comfortably shorter than that window and nothing more. Half a minute costs a
 * visitor two tiny requests a minute while they are looking at a conversation,
 * and nothing at all when they are not.
 */
const PRESENCE_INTERVAL_MS = 30_000;

/**
 * Tells the API what the visitor has in front of them.
 *
 * It is what stops support from saying the same thing twice. An agent's reply
 * is normally emailed as well; one that this panel has already shown is not,
 * and one that arrives while the thread is open is given a few minutes to be
 * read here before it is. Both decisions are the API's — all this does is
 * report two facts it cannot know by itself: which turn was the last one drawn,
 * and that the thread is still on screen.
 *
 * ── Which turn is named ─────────────────────────────────────────────────────
 *
 * The newest one somebody ELSE wrote, not simply the last in the list. Those
 * are different after the visitor sends: their own message goes to the end of
 * the list whatever arrived unseen before it, so naming it would claim they had
 * read a reply this panel never drew. A turn from the other side can only be in
 * the list because the API handed it over inside a whole thread, which makes it
 * honest evidence of everything before it.
 *
 * The API enforces the same rule and does not rely on this — a visitor's own
 * message marks nothing read there. Naming the right turn here is what earns
 * the credit when it is due: a thread reopened to `[their reply, my follow-up]`
 * has been read through their reply, and says so.
 *
 * When the other side has not written yet there is only the visitor's own
 * message to name. That still says "here", which is all there is to say.
 *
 * Reported in three moments:
 *
 * - **a newer turn was drawn** — the thread loaded, or a message was appended
 *   to it. That is the read receipt;
 * - **on a timer while the thread stays open** — the same call, saying nothing
 *   new was read but somebody is still looking;
 * - **the tab came back into view** — so presence resumes at once rather than
 *   up to an interval late.
 *
 * And never while the page is hidden. A thread open in a background tab is not
 * being read, and saying otherwise would hold back an email from someone who
 * walked away.
 *
 * Every failure is swallowed, and that is the right way round: the worst a lost
 * report can do is let an email through that turned out not to be needed. An
 * error surfaced here would interrupt somebody reading a conversation to tell
 * them about bookkeeping. It also covers the one failure that is expected —
 * a contact API a release ahead of its database, for the moments that lasts.
 *
 * The panel renders only while it is open and unmounts this screen when the
 * visitor goes back to the list, so "mounted" already means "this thread is
 * the one on screen"; the page-visibility check is the only extra condition.
 */
export const useThreadReadReceipts = (
  ticketId: () => string,
  messages: Ref<WidgetMessage[]>,
): void => {
  let timer: number | undefined;

  const shownThroughId = (): string | undefined => {
    const list = messages.value;
    const fromTheOtherSide = [...list].reverse().find((message) => !isOwnMessage(message));

    return (fromTheOtherSide ?? list[list.length - 1])?.id;
  };

  const report = async (): Promise<void> => {
    const messageId = shownThroughId();

    if (!messageId || document.visibilityState !== 'visible') {
      return;
    }

    try {
      await markTicketRead(ticketId(), messageId);
    } catch {
      // Deliberately nothing — see above.
    }
  };

  const onVisibilityChange = (): void => {
    void report();
  };

  watch(shownThroughId, (messageId) => {
    if (messageId) {
      void report();
    }
  });

  onMounted(() => {
    timer = window.setInterval(() => void report(), PRESENCE_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onBeforeUnmount(() => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });
};
